import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Box, useTheme } from "@mui/material";
import { createPortal } from "react-dom";

import type { CanvasBackgroundTheme } from "../BackgroundAnimations/canvasBackgroundTheme";

import { createBurstEngine } from "./burstEngine";
import type { BurstOrigin, ThemedBurstType } from "./themedBurstParticles";

export type BurstType = "heart" | "sparkle" | "note" | "card-die" | "connector" | ThemedBurstType;

export interface BurstFromClientRectOptions {
  align?: "center" | "left";
  count?: number;
  type?: BurstType;
}

const LEFT_ALIGN_MAX_OFFSET_PX = 36;

export interface BurstHandle {
  burstAt: (x: number, y: number, count?: number, type?: BurstType) => void;
  burstAtClientPoint: (clientX: number, clientY: number, count?: number, type?: BurstType) => void;
  burstFromEvent: (event: { clientX: number; clientY: number }, count?: number, type?: BurstType) => void;
  burstFromClientRect: (
    rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
    options?: BurstFromClientRectOptions
  ) => void;
  setType: (type: BurstType | "none") => void;
}

export interface BurstProps {
  type?: BurstType | "none";
  hue?: number;
  theme?: CanvasBackgroundTheme;
  color?: string | null;
  count?: number;
}

export const Burst = forwardRef<BurstHandle, BurstProps>(function Burst(
  { type = "heart", hue = 0, theme = "dark", color = null, count = 7 },
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const typeRef = useRef<BurstType | "none">(type);
  const spawnRef = useRef<((origin: BurstOrigin, count?: number, typeOverride?: BurstType) => void) | null>(null);
  const muiTheme = useTheme();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const engine = createBurstEngine(canvas, {
      hue,
      theme,
      color,
      count,
      getType: () => typeRef.current,
    });
    if (!engine) {
      return;
    }
    spawnRef.current = engine.spawn;

    return () => {
      engine.destroy();
      spawnRef.current = null;
    };
  }, [hue, theme, color, count, isMounted]);

  useImperativeHandle(ref, () => {
    const spawnAtClientPoint = (clientX: number, clientY: number, particleCount?: number, typeOverride?: BurstType) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const canvasRect = canvas.getBoundingClientRect();
      const x = clientX - canvasRect.left;
      const y = clientY - canvasRect.top;
      spawnRef.current?.({ x, y, rect: { left: x, bottom: y, width: 0 } }, particleCount, typeOverride);
    };

    return {
      burstAt(x, y, particleCount, typeOverride) {
        spawnRef.current?.({ x, y, rect: { left: x, bottom: y, width: 0 } }, particleCount, typeOverride);
      },
      burstAtClientPoint: spawnAtClientPoint,
      burstFromEvent(event, particleCount, typeOverride) {
        spawnAtClientPoint(event.clientX, event.clientY, particleCount, typeOverride);
      },
      burstFromClientRect(rect, options) {
        const canvas = canvasRef.current;
        if (!canvas) {
          return;
        }
        const canvasRect = canvas.getBoundingClientRect();
        const left = rect.left - canvasRect.left;
        const top = rect.top - canvasRect.top;
        const x =
          options?.align === "left"
            ? left + Math.min(rect.width * 0.2, LEFT_ALIGN_MAX_OFFSET_PX)
            : left + rect.width / 2;
        spawnRef.current?.(
          { x, y: top + rect.height / 2, rect: { left, bottom: top + rect.height, width: rect.width } },
          options?.count,
          options?.type
        );
      },
      setType(newType) {
        typeRef.current = newType;
      },
    };
  });

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: muiTheme.zIndex.modal + 1,
      }}
    />,
    document.body
  );
});
